import { getLoginContext } from '@athena-lib/loginContext.svelte';
import type { CheckoutErrors } from './Checkout.svelte';

type FormInteractionEvent =
  | 'form_error'
  | 'form_shown'
  | 'form_started'
  | 'form_completed'
  | 'form_submitted';
type UserTypes = 'business' | 'private';
export const getCheckoutGtmStateMachine = () => {
  const submittedUniqueEvents: Array<FormInteractionEvent> = [];
  let userType: UserTypes = 'private';
  const submitUniqueEvent = (eventType: FormInteractionEvent) => {
    if (!submittedUniqueEvents.includes(eventType)) {
      triggerGtmFormInterActionEvent(eventType, userType);
      submittedUniqueEvents.push(eventType);
    }
  };
  let previousErrors: Array<string> = [];
  submitUniqueEvent('form_shown');
  return {
    setUserType: (newUserType: UserTypes) => {
      userType = newUserType;
    },
    reportFormErrors: (checkoutForm: CheckoutErrors) => {
      // we don't want to report already reported errors
      const currentErrors = collectErrors(checkoutForm);
      const newErrors = currentErrors.filter((error) => !previousErrors.includes(error));
      previousErrors = currentErrors;
      newErrors.forEach((error) => {
        triggerGtmFormInterActionEvent('form_error', userType, error);
      });
    },
    // any field changed
    startForm: () => submitUniqueEvent('form_started'),
    // the form is valid
    completeForm: () => {
      submitUniqueEvent('form_started');
      submitUniqueEvent('form_completed');
    },
    submitForm: () => {
      submitUniqueEvent('form_started');
      submitUniqueEvent('form_completed');
      submitUniqueEvent('form_submitted');
    },
  };
};

export const reportInvalidGiftCardOrPromoCode = (user_type: UserTypes) => {
  triggerGtmFormInterActionEvent('form_error', user_type, 'invalid gift card or promo code');
};

const collectErrors = (
  checkoutErrors: Record<string, unknown>,
  path: Array<string> = [],
): Array<string> => {
  const values = Object.entries(checkoutErrors);
  return values.flatMap(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      return collectErrors(value as Record<string, unknown>, [...path, key]);
    }
    if (typeof value === 'string') {
      return [[[...path, key].join('.'), value].join(': ') satisfies string];
    }
    return [];
  });
};

function triggerGtmFormInterActionEvent(
  interactionType: FormInteractionEvent,
  user_type: UserTypes,
  errorType?: unknown,
) {
  const signedIn = getLoginContext().isLoggedIn;
  const eventParams: Record<string, string> = {
    interaction_type: interactionType,
    form_type: 'checkout_form',
    interaction_category: 'form',
    checkout_version: '2',
    checkout_user_type: user_type,
    signed_in: signedIn.toString(),
  };

  if (errorType) {
    eventParams.error_type = typeof errorType === 'string' ? errorType : JSON.stringify(errorType);
  }

  document.dispatchEvent(
    new CustomEvent('gtm-event', {
      detail: {
        event: 'form_interaction',
        event_params: eventParams,
      },
    }),
  );
}
